// views/ConfigurationManagement.tsx

import React, {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Breadcrumb, Button, Card, Divider, Empty, Input, Modal, Space, Tag, Tooltip, Typography, App} from 'antd';
import {
    ExclamationCircleOutlined,
    FilterOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SyncOutlined
} from '@ant-design/icons';
import {debounce} from 'lodash';
import {useActionFlowConfigurations, useManageConfiguration} from '../hooks/useConfiguration';
import {ActionFlowConfigurationDTO, ConfigurationFilter} from '../types/configuration';
import {showErrorNotification, showSuccessNotification} from '@/shared/utils/notify';
import ConfigurationList from '../components/list/ConfigurationList';
import FilterDrawer from '../components/filter/FilterDrawer';

const { Title, Text } = Typography;
const { Search } = Input;

const ConfigurationManagement: React.FC = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<ConfigurationFilter>({});
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
    const { configurations, isLoading, error, refetch } = useActionFlowConfigurations(filter);
    const { deleteConfiguration } = useManageConfiguration();

    // Handle search
    const handleSearch = useCallback(
        debounce((value: string) => {
            setFilter(prev => ({
                ...prev,
                name: value || undefined
            }));
            setSearchTerm(value);
        }, 500),
        []
    );

    // Handle create new configuration
    const handleCreateNew = () => {
        navigate('/admin/action-flow/configurations/new');
    };

    // Handle edit configuration
    const handleEdit = (id: string) => {
        navigate(`/admin/action-flow/editor/${id}`);
    };

    // Handle view configuration details
    const handleViewDetails = (id: string) => {
        navigate(`/admin/action-flow/configurations/${id}`);
    };

    const { modal } = App.useApp();

    // Handle duplicate configuration
    const handleDuplicate = (configuration: ActionFlowConfigurationDTO) => {
        modal.confirm({
            title: 'Duplicate Configuration',
            icon: <InfoCircleOutlined />,
            content: `Do you want to create a copy of "${configuration.name}"?`,
            onOk: async () => {
                try {
                    // Create a new configuration based on the existing one
                    const newConfig = {
                        ...configuration,
                        id: undefined,
                        name: `${configuration.name} (Copy)`,
                        version: `${configuration.version}-copy`
                    };

                    // You would add this to your useManageConfiguration hook
                    // await duplicateConfiguration(newConfig);
                    showSuccessNotification('Configuration duplicated successfully');
                    refetch();
                } catch (error) {
                    showErrorNotification('Failed to duplicate configuration');
                }
            }
        });
    };

    // Handle delete configuration
    const handleDelete = (id: string, name: string) => {
        modal.confirm({
            title: 'Delete Configuration',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteConfiguration(id);
                    showSuccessNotification('Configuration deleted successfully');
                } catch (error) {
                    showErrorNotification('Failed to delete configuration');
                }
            }
        });
    };

    // Download configuration as JSON
    const handleDownload = (configuration: ActionFlowConfigurationDTO) => {
        try {
            const configData = JSON.parse(configuration.configurationJson);
            const jsonString = JSON.stringify(configData, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = `${configuration.name.replace(/\s+/g, '_')}_v${configuration.version}.json`;
            document.body.appendChild(link);
            link.click();

            // Clean up
            setTimeout(() => {
                URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 100);

            showSuccessNotification('Configuration downloaded successfully');
        } catch (error) {
            showErrorNotification('Failed to download configuration');
        }
    };

    // Apply filter changes
    const handleFilterChange = (newFilter: ConfigurationFilter) => {
        setFilter({
            ...filter,
            ...newFilter
        });
        setIsFilterDrawerOpen(false);
    };

    // Reset all filters
    const handleResetFilters = () => {
        setFilter({});
        setSearchTerm('');
        setIsFilterDrawerOpen(false);
    };

    if (error) {
        return (
            <Card className="error-card m-6">
                <Alert
                    message="Error Loading Configurations"
                    description={(error as Error)?.message || 'Failed to load workflow configurations. Please try again later.'}
                    type="error"
                    showIcon
                />
                <div className="mt-4">
                    <Button onClick={() => refetch()} icon={<SyncOutlined />}>
                        Retry
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header with breadcrumb */}
            <div className="mb-6">
                <Breadcrumb
                    className="mb-2"
                    items={[
                        {
                            href: '/',
                            title: <HomeOutlined />
                        },
                        {
                            href: '/admin',
                            title: 'Admin'
                        },
                        {
                            title: 'Action Flow'
                        },
                        {
                            title: 'Configurations'
                        }
                    ]}
                />

                <div className="flex justify-between items-center">
                    <Title level={2} className="m-0">
                        Action Flow Configurations
                    </Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateNew}
                        size="large"
                    >
                        Create New Configuration
                    </Button>
                </div>

                <Text type="secondary">
                    Manage workflow configurations for automated processes and approvals
                </Text>
            </div>

            <Divider />

            {/* Search and filter controls */}
            <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
                <div className="flex-grow max-w-md">
                    <Search
                        placeholder="Search by name"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        allowClear
                        enterButton
                    />
                </div>

                <Space>
                    <Tooltip title="Apply filters">
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setIsFilterDrawerOpen(true)}
                            className={Object.keys(filter).length > 1 ? 'border-blue-500 text-blue-500' : ''}
                        >
                            Filters
                            {Object.keys(filter).length > 1 && (
                                <Tag className="ml-2">{Object.keys(filter).length - 1}</Tag>
                            )}
                        </Button>
                    </Tooltip>

                    <Tooltip title="Refresh list">
                        <Button icon={<SyncOutlined />} onClick={() => refetch()} />
                    </Tooltip>
                </Space>
            </div>

            {/* Configuration list */}
            <Card bordered={false} className="shadow-md">
                <ConfigurationList
                    // @ts-ignore
                    configurations={configurations}
                    isLoading={isLoading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onView={handleViewDetails}
                    onDuplicate={handleDuplicate}
                    onDownload={handleDownload}
                />

                {configurations?.length === 0 && !isLoading && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            Object.keys(filter).length > 0 ? (
                                <span>
                  No configurations match your filters. <Button type="link" onClick={handleResetFilters}>Clear filters</Button>
                </span>
                            ) : (
                                <span>
                  No configurations found. Click "Create New Configuration" to get started.
                </span>
                            )
                        }
                    />
                )}
            </Card>

            {/* Filter drawer */}
            <FilterDrawer
                open={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                currentFilter={filter}
                onApply={handleFilterChange}
                onReset={handleResetFilters}
            />
        </div>
    );
};

export default ConfigurationManagement;